<template>
  <div class="admin-panel">
    <div class="loader" v-if="loading"><div class="spinner"></div></div>

    <template v-if="!selected">
      <table class="admin-table">
        <thead>
          <tr>
            <th>ID</th>
            <th>Название</th>
            <th>Истекает</th>
            <th>Статус</th>
            <th>Файлов</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="vs in items" :key="vs.id" @click="select(vs)" class="clickable">
            <td class="mono">{{ vs.id }}</td>
            <td>
              {{ vs.name }}
              <span v-if="vs.id === documentationVectorStoreId" class="badge">используется</span>
            </td>
            <td>{{ formatDate(vs.expiresAt) }}</td>
            <td>{{ vs.status }}</td>
            <td>{{ vs.fileCounts ? vs.fileCounts.total : '-' }}</td>
            <td>
              <button
                v-if="vs.id !== documentationVectorStoreId"
                class="primary small"
                @click.stop="use(vs)"
                :disabled="using"
              >Использовать</button>
            </td>
          </tr>
        </tbody>
      </table>

      <div class="pagination">
        <button :disabled="cursorStack.length === 0" @click="prevPage">&larr; Назад</button>
        <button :disabled="!hasMore" @click="nextPage">Вперёд &rarr;</button>
      </div>
    </template>

    <div v-else class="detail">
      <button @click="selected = null">&larr; К списку</button>
      <h3>{{ selected.name }} <span class="mono id-inline">({{ selected.id }})</span></h3>
      <span v-if="selected.id === documentationVectorStoreId" class="badge">используется как текущая база документации</span>

      <div class="edit-form">
        <label>Название <input v-model="editName" /></label>
        <label>Срок действия, дней с последней активности <input type="number" v-model.number="editExpireDays" /></label>
        <button class="primary" @click="save">Сохранить</button>
      </div>

      <pre class="raw-json">{{ JSON.stringify(selected, null, 2) }}</pre>

      <button class="danger" @click="deleteStore">Удалить Vector Store</button>

      <h4>Файлы в Vector Store</h4>
      <table class="admin-table">
        <thead><tr><th>ID</th><th>Статус</th><th>Атрибуты</th></tr></thead>
        <tbody>
          <tr v-for="f in files" :key="f.id">
            <td class="mono">{{ f.id }}</td>
            <td>{{ f.status }}</td>
            <td>{{ JSON.stringify(f.attributes) }}</td>
          </tr>
        </tbody>
      </table>
      <div class="pagination">
        <button :disabled="fileCursorStack.length === 0" @click="prevFilePage">&larr; Назад</button>
        <button :disabled="!filesHasMore" @click="nextFilePage">Вперёд &rarr;</button>
      </div>
    </div>
  </div>
</template>

<script>
import { adminApi } from '../api/admin-api';
import Vue from 'vue';

export default {
  name: 'vector-store-list',
  props: {
    documentationVectorStoreId: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      items: [],
      loading: false,
      after: null,
      cursorStack: [],
      hasMore: false,
      lastId: null,

      selected: null,
      editName: '',
      editExpireDays: null,

      files: [],
      fileAfter: null,
      fileCursorStack: [],
      filesHasMore: false,
      fileLastId: null,

      using: false,
    };
  },
  methods: {
    formatDate(iso) {
      if (!iso) return '—';
      return new Date(iso).toLocaleString('ru-RU');
    },
    async use(vs) {
      if (this.using || vs.id === this.documentationVectorStoreId) return;
      if (!confirm(`Использовать Vector Store "${vs.name}" как базу документации для чата? Настройка будет сразу применена и сохранена в appsettings.json.`)) return;

      this.using = true;
      try {
        await adminApi.post(`/vectorStores/${vs.id}/use`);
        this.$emit('used', vs.id);
        Vue.toasted.show('Vector Store назначен как база документации', { type: 'success', duration: 3000 });
      } catch (e) {
        Vue.toasted.show('Не удалось назначить Vector Store', { type: 'error', duration: 5000 });
      } finally {
        this.using = false;
      }
    },
    async load(after) {
      this.loading = true;
      try {
        const { data } = await adminApi.get('/vectorStores', { params: { after: after || undefined, limit: 20 } });
        this.items = data.data;
        this.hasMore = data.hasMore;
        this.lastId = data.lastId;
      } finally {
        this.loading = false;
      }
    },
    nextPage() {
      this.cursorStack.push(this.after);
      this.after = this.lastId;
      this.load(this.after);
    },
    prevPage() {
      this.after = this.cursorStack.pop();
      this.load(this.after);
    },
    async select(vs) {
      this.selected = vs;
      this.editName = vs.name;
      this.editExpireDays = vs.expiresAfterDays;
      this.files = [];
      this.fileAfter = null;
      this.fileCursorStack = [];
      await this.loadFiles();
    },
    async loadFiles(after) {
      const { data } = await adminApi.get(`/vectorStores/${this.selected.id}/files`, { params: { after: after || undefined, limit: 20 } });
      this.files = data.data;
      this.filesHasMore = data.hasMore;
      this.fileLastId = data.lastId;
    },
    nextFilePage() {
      this.fileCursorStack.push(this.fileAfter);
      this.fileAfter = this.fileLastId;
      this.loadFiles(this.fileAfter);
    },
    prevFilePage() {
      this.fileAfter = this.fileCursorStack.pop();
      this.loadFiles(this.fileAfter);
    },
    async save() {
      const { data } = await adminApi.put(`/vectorStores/${this.selected.id}`, {
        name: this.editName,
        expireAfterDays: this.editExpireDays,
      });
      this.selected = data;
      Vue.toasted.show('Сохранено', { type: 'success', duration: 2000 });
      await this.load(this.after);
    },
    async deleteStore() {
      if (this.selected.id === this.documentationVectorStoreId) {
        Vue.toasted.show('Этот Vector Store используется как текущая база документации и не может быть удалён.', { type: 'error', duration: 5000 });
        return;
      }

      if (!confirm(`Точно удалить Vector Store "${this.selected.name}"? Это действие нельзя отменить.`)) {
        return;
      }

      const deleteFiles = confirm('Также удалить все файлы, привязанные к этому Vector Store? Учтите: эти файлы могут использоваться и в других Vector Store — они тоже лишатся этих файлов.\n\nOK — удалить и файлы, Отмена — удалить только Vector Store.');

      await adminApi.delete(`/vectorStores/${this.selected.id}`, { params: { deleteFiles } });
      Vue.toasted.show('Vector Store удалён', { type: 'success', duration: 3000 });
      this.selected = null;
      await this.load(null);
    },
  },
  mounted() {
    this.load(null);
  }
};
</script>

<style scoped>
.admin-panel {
  position: relative;
  padding: 10px;
}

.admin-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
}

.admin-table th, .admin-table td {
  text-align: left;
  padding: 8px 10px;
  border-bottom: 1px solid #e0e0e0;
}

.clickable {
  cursor: pointer;
}

.clickable:hover {
  background: #f5f5f5;
}

.badge {
  display: inline-block;
  margin-left: 8px;
  padding: 2px 6px;
  font-size: 11px;
  border-radius: 6px;
  background: #ffe9c7;
  color: #8a5a00;
}

.pagination {
  margin-top: 10px;
  display: flex;
  gap: 10px;
}

.mono {
  font-family: monospace;
  font-size: 12px;
}

.id-inline {
  color: #888;
  font-weight: normal;
}

.raw-json {
  background: #f7f7f7;
  padding: 10px;
  border-radius: 6px;
  max-height: 250px;
  overflow: auto;
  font-size: 12px;
}

.edit-form {
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-width: 400px;
  margin: 15px 0;
}

.edit-form label {
  display: flex;
  flex-direction: column;
  font-size: 12px;
  gap: 4px;
}

button {
  cursor: pointer;
}

button.primary {
  background: #f15540;
  color: white;
  border: none;
  border-radius: 5px;
  padding: 8px 14px;
}

button.small {
  padding: 4px 10px;
  font-size: 12px;
}

button.danger {
  background: #fff;
  color: #d94a36;
  border: 1px solid #d94a36;
  border-radius: 5px;
  padding: 6px 12px;
  margin: 10px 0;
}

.loader {
  position: absolute;
  top: 10px;
  right: 10px;
}

.spinner {
  width: 20px;
  height: 20px;
  border: 3px solid rgba(0,0,0,0.1);
  border-left-color: #e74c3c;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}
</style>



// WEBPACK FOOTER //
// src/components/admin/vector-store-list.vue